import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';

import {
  Card, BlockStack, InlineStack, Text, Button, Tabs, IndexTable,
  Badge, Banner, Select, TextField, Box,
} from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';

import { batchesIndex } from '../../redux/actions/batches';
import { auditLogsIndex } from '../../redux/actions/auditLogs';

const formatTime = (value) => value ? new Date(value).toLocaleString() : '-';

const TABS = [
  { id: 'batches', content: 'Completed Batches' },
  { id: 'actions', content: 'Action History' }
];

const OBJECT_CLASS_OPTIONS = [
  { label: 'All objects', value: '' },
  { label: 'Batches', value: 'Batch' },
  { label: 'Totes', value: 'Tote' },
  { label: 'Order shipments', value: 'OrderShipment' },
  { label: 'Prepared shipments', value: 'PreparedOrderShipment' },
  { label: 'Packing rules', value: 'PackingRule' },
  { label: 'Packing photos', value: 'PackingPhoto' },
  { label: 'Cart layouts', value: 'CartLayout' },
  { label: 'Warehouse users', value: 'WarehouseUser' },
  { label: 'Login codes', value: 'WarehouseLoginCode' },
  { label: 'Sessions', value: 'UserSession' },
  { label: 'Settings', value: 'Settings' },
];

const SOURCE_OPTIONS = [
  { label: 'All sources', value: '' },
  { label: 'Shopify admin', value: 'shopify_admin' },
  { label: 'Warehouse app', value: 'warehouse_app' },
];

const SOURCE_LABELS = {
  shopify_admin: 'Admin',
  warehouse_app: 'Warehouse app',
};

const ACTION_LABELS = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  login: 'Login',
};

const auditAction = (log) => ACTION_LABELS[log.action] || log.action.replaceAll('_', ' ');

const auditObject = (log) => {
  const label = log.object_id ? `${log.object_class} ${log.object_id.slice(-8)}` : log.object_class;

  if (log.object_class === 'Batch' && log.object_id) {
    return <Link to={`/batches/${log.object_id}`}>{label}</Link>;
  }

  return label;
};

const CHANGES_PREVIEW_COUNT = 2;
const CHANGE_LINE_LIMIT = 80;

const formatChange = ([field, change]) => `${field}: ${JSON.stringify(change.from)} → ${JSON.stringify(change.to)}`;

const truncateLine = (line) => line.length > CHANGE_LINE_LIMIT ? `${line.slice(0, CHANGE_LINE_LIMIT)}…` : line;

// Long diffs collapse to a couple of truncated lines with a Show more
// toggle - the full tuple text only renders once expanded
const AuditChanges = ({ changes }) => {
  const [expanded, setExpanded] = useState(false);
  const lines = Object.entries(changes || {}).map(formatChange);

  if (lines.length === 0) {
    return <Text as="span" variant="bodySm">-</Text>;
  }

  const visible = expanded ? lines : lines.slice(0, CHANGES_PREVIEW_COUNT).map(truncateLine);
  const hiddenCount = lines.length - visible.length;
  const wasTruncated = !expanded && visible.some((line, i) => line !== lines[i]);
  const hasMore = hiddenCount > 0 || wasTruncated;

  return (
    <BlockStack gap="100">
      {visible.map((line, index) => <Text as="p" variant="bodySm" key={index}>{line}</Text>)}
      {hasMore &&
        <Button variant="plain" onClick={() => setExpanded(true)}>
          {hiddenCount > 0 ? `+${hiddenCount} more` : 'Show more'}
        </Button>
      }
      {expanded &&
        <Button variant="plain" onClick={() => setExpanded(false)}>
          Show less
        </Button>
      }
    </BlockStack>
  );
};

const History = ({ batches, auditLogs, loadBatches, loadAuditLogs }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [objectClass, setObjectClass] = useState('');
  const [source, setSource] = useState('');
  const [objectId, setObjectId] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const auditFilters = useMemo(() => ({
    objectClass: objectClass,
    objectId: objectId.trim(),
    userEmail: userEmail.trim(),
    source: source,
  }), [objectClass, objectId, userEmail, source]);

  useEffect(() => {
    if (activeTab === 0) {
      loadBatches('complete');
    }
  }, [activeTab, loadBatches]);

  // Debounce the text inputs so we do not call the API on every keystroke
  useEffect(() => {
    if (activeTab !== 1) {
      return;
    }

    const timer = setTimeout(() => loadAuditLogs(auditFilters), 300);
    return () => clearTimeout(timer);
  }, [activeTab, objectClass, source, objectId, userEmail, auditFilters, loadAuditLogs]);

  // Batch ids are UUIDv7 - time-ordered - so sorting by id descending
  // puts the most recently created batches first
  const completedBatches = [...batches.batches].sort((a, b) => b.id.localeCompare(a.id));

  return (
    <SettingsPage
      title="History"
      description="Have questions or need some help? Contact us at support@athousandapps.com"
    >
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">History</Text>
          <Text as="p" variant="bodyMd">Recently completed batches and the audit log of actions taken by warehouse users and admins.</Text>

          <Tabs tabs={TABS} selected={activeTab} onSelect={setActiveTab}>
            <Box paddingBlockStart="400">
              { activeTab === 0 &&
                <BlockStack gap="400">
                  <InlineStack align="start">
                    <Button
                      variant="plain"
                      icon={RefreshIcon}
                      loading={batches.isFetching}
                      onClick={() => loadBatches('complete')}
                    >
                      Refresh
                    </Button>
                  </InlineStack>

                  { batches.errors &&
                    <Banner
                      title="Batch request failed"
                      tone="critical"
                    >
                      <p>{batches.errors}</p>
                    </Banner>
                  }

                  { batches.isFetching &&
                    <LoadingSpinner size="small" statusText="Loading batches"/>
                  }

                  { batches.status === 'success' && completedBatches.length > 0 &&
                    <IndexTable
                      resourceName={{ singular: 'batch', plural: 'batches' }}
                      itemCount={completedBatches.length}
                      selectable={false}
                      headings={[
                        { title: 'Batch' },
                        { title: 'Totes' },
                        { title: 'Items' },
                        { title: 'Completed' },
                        { title: 'Outcome' },
                        { title: 'Created' },
                      ]}
                    >
                      {completedBatches.map((batch, index) => (
                        <IndexTable.Row id={batch.id} key={batch.id} position={index}>
                          <IndexTable.Cell><Link to={`/batches/${batch.id}`}>{batch.name || batch.id}</Link></IndexTable.Cell>
                          <IndexTable.Cell>{batch.tote_count}</IndexTable.Cell>
                          <IndexTable.Cell>{batch.item_count}</IndexTable.Cell>
                          <IndexTable.Cell>{formatTime(batch.complete_at)}</IndexTable.Cell>
                          <IndexTable.Cell>
                            {batch.returned_at
                              ? <Badge tone="success">Returned to stock</Badge>
                              : batch.set_aside_at
                                ? <Badge>Set aside</Badge>
                                : (batch.return_items || []).length > 0
                                  ? <Badge tone="success">Returned to stock</Badge>
                                  : <Badge tone="success">Packed</Badge>
                            }
                          </IndexTable.Cell>
                          <IndexTable.Cell>{formatTime(batch.created_at)}</IndexTable.Cell>
                        </IndexTable.Row>
                      ))}
                    </IndexTable>
                  }

                  { batches.status === 'success' && !batches.isFetching && completedBatches.length === 0 &&
                    <Banner tone="warning">
                      <p>No completed batches yet.</p>
                    </Banner>
                  }
                </BlockStack>
              }

              { activeTab === 1 &&
                <BlockStack gap="400">
                  <Text as="p" variant="bodySm">Action history records are kept for 90 days.</Text>

                  <InlineStack gap="300" wrap>
                    <div style={{ minWidth: '180px' }}>
                      <Select
                        label="Object"
                        options={OBJECT_CLASS_OPTIONS}
                        value={objectClass}
                        onChange={setObjectClass}
                      />
                    </div>
                    <div style={{ minWidth: '180px' }}>
                      <Select
                        label="Source"
                        options={SOURCE_OPTIONS}
                        value={source}
                        onChange={setSource}
                      />
                    </div>
                    <div style={{ minWidth: '180px' }}>
                      <TextField
                        label="Object ID"
                        placeholder="Filter by object id"
                        value={objectId}
                        onChange={setObjectId}
                        autoComplete="off"
                      />
                    </div>
                    <div style={{ minWidth: '180px' }}>
                      <TextField
                        label="User"
                        placeholder="Filter by user email"
                        value={userEmail}
                        onChange={setUserEmail}
                        autoComplete="off"
                      />
                    </div>
                  </InlineStack>

                  { auditLogs.errors &&
                    <Banner
                      title="Action history request failed"
                      tone="critical"
                    >
                      <p>{auditLogs.errors}</p>
                    </Banner>
                  }

                  { auditLogs.isFetching &&
                    <LoadingSpinner size="small" statusText="Loading action history"/>
                  }

                  { auditLogs.status === 'success' && auditLogs.auditLogs.length > 0 &&
                    <IndexTable
                      resourceName={{ singular: 'action', plural: 'actions' }}
                      itemCount={auditLogs.auditLogs.length}
                      selectable={false}
                      headings={[
                        { title: 'Time' },
                        { title: 'User' },
                        { title: 'Action' },
                        { title: 'Object' },
                        { title: 'Source' },
                        { title: 'Changes' },
                      ]}
                    >
                      {auditLogs.auditLogs.map((log, index) => (
                        <IndexTable.Row id={`${log.object_id}-${log.occurred_at}-${index}`} key={index} position={index}>
                          <IndexTable.Cell>{formatTime(log.occurred_at)}</IndexTable.Cell>
                          <IndexTable.Cell>{log.actor || '-'}</IndexTable.Cell>
                          <IndexTable.Cell>{auditAction(log)}</IndexTable.Cell>
                          <IndexTable.Cell>{auditObject(log)}</IndexTable.Cell>
                          <IndexTable.Cell><Badge>{SOURCE_LABELS[log.source] || log.source}</Badge></IndexTable.Cell>
                          <IndexTable.Cell><AuditChanges changes={log.changes} /></IndexTable.Cell>
                        </IndexTable.Row>
                      ))}
                    </IndexTable>
                  }

                  { auditLogs.status === 'success' && !auditLogs.isFetching && auditLogs.auditLogs.length === 0 &&
                    <Banner tone="warning">
                      <p>No actions recorded yet.</p>
                    </Banner>
                  }

                  { auditLogs.continuationToken &&
                    <InlineStack align="start">
                      <Button
                        loading={auditLogs.isFetchingMore}
                        onClick={() => loadAuditLogs(auditFilters, auditLogs.continuationToken, true)}
                      >
                        Load more
                      </Button>
                    </InlineStack>
                  }
                </BlockStack>
              }
            </Box>
          </Tabs>
        </BlockStack>
      </Card>
    </SettingsPage>
  )
}

const mapStateToProps = state => ({
  batches: state.batches,
  auditLogs: state.auditLogs,
});

const mapDispatchToProps = (dispatch) => ({
  loadBatches: (status) => dispatch(batchesIndex(status)),
  loadAuditLogs: (filters, continuationToken, append) => dispatch(auditLogsIndex(filters, continuationToken, append)),
});

export default connect(mapStateToProps, mapDispatchToProps)(History);
